import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import {
  importsAPI,
  type DashboardSummary,
  type ImportJob,
  type MiddlewareQueue,
  type MiddlewareStatusResponse,
  type ImportSource,
  type ImportSourceOption,
} from '../../lib/api/imports';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { scopeImportOptions, defaultImportOptionKey } from './importMonitorScope';
import { dateRangeParams, EMPTY_DATE_RANGE, type DateRange } from './importDateRange';
import JobDetailModal from './ImportJobDetailModal';
import { JobsTable } from './ImportDashboardTables';
import ImportAutoCancelledTab from './ImportAutoCancelledTab';
import ImportDateInconsistencyTab from './ImportDateInconsistencyTab';
import ImportScheduledJobsTab from './ImportScheduledJobsTab';
import ImportErpBalanceTab from './ImportErpBalanceTab';
import ImportErpReceivedTab from './ImportErpReceivedTab';
import ImportCollabHoldsTab from './ImportCollabHoldsTab';
import ImportRulesTab from './ImportRulesTab';
import ImportOrderLookupTab from './ImportOrderLookupTab';
import ImportQueueSearchTab from './ImportQueueSearchTab';
import ImportHealthCheckTab from './ImportHealthCheckTab';
import ImportWritebackTab from './ImportWritebackTab';
import { MiddlewareHealthPanel } from './MiddlewareHealthPanel';
import { DataAgeCard } from './DataAgeCard';
import {
  StatCards, DataTypeChart, MiddlewareStatus, RecentFailures, JobManagementCard,
} from './ImportDashboardCards';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ImportDashboard() {
  // Writeback off (installs whose middleware never collects them) means the
  // queue can never fill, so the tab would only ever show an empty state.
  const { writebackEnabled, featureFlags } = useMenuSettings();
  // Rules & Mappings exposes handler internals and raw feed samples —
  // platform superusers only (the endpoint enforces the same gate).
  const { user } = useAuth();
  const isSuperuser = !!user?.is_superuser;
  // ERP Balance Mismatch needs the ERP's open qty, which only DE's import captures.
  const erpBalanceEnabled = user?.import_handler === 'dennis_eagle';
  // Collaboration Holds only exists where schedules are the collaboration
  // unit — accepted proposals freeze slots against ERP pushes there.
  const collabHoldsEnabled = !!featureFlags.schedule_collaboration;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Selection is keyed on the option's `key`, not its `source`. Several
  // customers can share a source (every standard tenant is source 'pna'), so
  // keying on source made those options identical — picking the second one
  // changed nothing, and every customer-scoped tab kept showing the first
  // customer's data.
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recentJobs, setRecentJobs] = useState<ImportJob[]>([]);
  const [middleware, setMiddleware] = useState<MiddlewareStatusResponse | null>(null);
  const [dataAge, setDataAge] = useState<import('../../lib/api/imports').DataAgeResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'data-age' | 'jobs' | 'writeback' | 'lookup' | 'queues' | 'cancelled' | 'date-exception' | 'new-po-date-exception' | 'scheduled-jobs' | 'erp-balance' | 'erp-received' | 'collab' | 'rules' | 'health'>('overview');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [sourceOptions, setSourceOptions] = useState<ImportSourceOption[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsPageSize, setJobsPageSize] = useState(25);
  const [jobsSearchInput, setJobsSearchInput] = useState('');
  const [jobsSearch, setJobsSearch] = useState('');
  const [jobsTotalCount, setJobsTotalCount] = useState(0);
  const [jobsStatusFilter, setJobsStatusFilter] = useState<string>('');
  const [jobsDateRange, setJobsDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // CR-9030: the dropdown follows the top right view context.
  const {
    viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, accessibleSites, viewKey,
    isLoading: viewLoading,
  } = useView();
  // Helix operators keep the cross-tenant view: their own company has no imports.
  const unscoped = isSuperuser || user?.company_type === 'helix';
  const view = { viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, sites: accessibleSites, unscoped };
  const scopedOptions = scopeImportOptions(sourceOptions, view);
  // The view context starts on an empty default; select and fetch only once
  // it has loaded, or the first entry flashes up and a pick made meanwhile is lost.
  const ready = sourcesLoaded && !viewLoading;
  const fetchSeq = useRef(0);

  const selectedOption = scopedOptions.find(o => o.key === selectedKey);
  const source: ImportSource = selectedOption?.source ?? 'pna';
  const customerId = selectedOption?.customer_id ?? 0;
  // The top right company has no import data: say so rather than fall back
  // to the whole scope.
  const noSiteData = ready && sourceOptions.length > 0 && !selectedOption;

  const handleSearchChange = useCallback((value: string) => {
    setJobsSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setJobsSearch(value);
      setJobsPage(1);
    }, 400);
  }, []);

  // Fetch available import sources on mount
  useEffect(() => {
    importsAPI.getSources().then(setSourceOptions)
      .catch(() => {}).finally(() => setSourcesLoaded(true));
  }, []);

  // CR-9030: pre select the top right site, and follow it when it changes.
  // Keyed on viewKey so a manual pick in the dropdown is kept until then.
  useEffect(() => {
    if (!ready) return;
    setSelectedKey(defaultImportOptionKey(scopedOptions, view));
    setActiveTab('overview');
    setJobsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, sourceOptions, viewKey]);

  const fetchData = useCallback(async (isRefresh = false) => {
    // Wait for the dropdown to settle, so the first fetch is the right customer.
    if (!ready) return;
    if (noSiteData) {
      setLoading(false);
      return;
    }
    // A reply for a customer no longer selected must not overwrite the page.
    const seq = ++fetchSeq.current;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const jobFilters: import('../../lib/api/imports').ImportJobFilters = {
        ordering: '-created_at',
        group: 1,
        page: jobsPage,
        page_size: jobsPageSize,
        ...(jobsSearch ? { search: jobsSearch } : {}),
        ...(jobsStatusFilter ? { status: jobsStatusFilter } : {}),
        ...dateRangeParams(jobsDateRange),
        ...(customerId ? { customer: customerId } : {}),
      };

      const [dashData, jobsData, mwData, ageData] = await Promise.all([
        importsAPI.getDashboard(source, customerId || undefined),
        importsAPI.getJobs(jobFilters, source),
        source === 'pna'
          ? importsAPI.getMiddlewareStatus(source, customerId || undefined).catch(() => ({ queues: [], error: 'Redis unavailable' } as MiddlewareStatusResponse))
          : Promise.resolve({ queues: [] as MiddlewareQueue[] } as MiddlewareStatusResponse),
        // Avanti tenants only — everyone else gets {enabled:false} and no card.
        importsAPI.getDataAge().catch(() => null),
      ]);
      if (seq !== fetchSeq.current) return;

      setSummary(dashData);
      setRecentJobs(jobsData.results);
      setJobsTotalCount(jobsData.count);
      setMiddleware(mwData);
      setDataAge(ageData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load import data';
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [ready, noSiteData, source, customerId, jobsPage, jobsPageSize, jobsSearch, jobsStatusFilter, jobsDateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Live imports monitor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time overview of data imports from middleware and scheduled jobs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Customer:</span>
            <select
              value={selectedKey}
              onChange={(e) => { setSelectedKey(e.target.value); setActiveTab('overview'); setJobsPage(1); setJobsSearch(''); setJobsSearchInput(''); setJobsStatusFilter(''); setJobsDateRange(EMPTY_DATE_RANGE); }}
              className="px-3 py-2 text-sm font-medium border border-border rounded-lg bg-background shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
            >
              {noSiteData && <option value="">No import data for this company</option>}
              {ready && scopedOptions.length > 0 ? (
                scopedOptions.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.customer_name}</option>
                ))
              ) : (
                <option value="">{ready ? 'No import sources in your scope' : 'Loading...'}</option>
              )}
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {noSiteData ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No import data for this company. Pick a customer above, or switch company at the top right.
          </CardContent>
        </Card>
      ) : (<>
      {/* Stat Cards */}
      {summary && <StatCards summary={summary} middleware={middleware} />}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {/* Writeback is portal -> ERP traffic, so it only exists for the
            middleware-backed sources. The CSV wizard (data_import) has no
            writeback path at all. */}
        {(source === 'data_import'
          // CR-7468: `erp-received` belongs on EVERY source, including this one.
          // It reports what the ORDER FEED said it had received, and a CSV-fed
          // customer has an order feed like any other — the tenant this work was
          // raised for is on this branch, and leaving the tab off it made the
          // page invisible exactly where it was needed (caught in the browser,
          // not by a test).
          ? (['overview', 'jobs', 'lookup', 'queues', 'cancelled', 'date-exception', 'new-po-date-exception', 'scheduled-jobs', 'erp-received', 'rules', 'health'] as const)
          : writebackEnabled
          ? (['overview', 'jobs', 'data-age', 'writeback', 'lookup', 'queues', 'cancelled', 'date-exception', 'new-po-date-exception', 'scheduled-jobs', 'erp-balance', 'erp-received', 'collab', 'rules', 'health'] as const)
          : (['overview', 'jobs', 'data-age', 'lookup', 'queues', 'cancelled', 'date-exception', 'new-po-date-exception', 'scheduled-jobs', 'erp-balance', 'erp-received', 'collab', 'rules', 'health'] as const)
        ).filter(tab => tab !== 'collab' || collabHoldsEnabled)
          .filter(tab => tab !== 'erp-balance' || erpBalanceEnabled)
          .filter(tab => tab !== 'rules' || isSuperuser)
          // CR-7468: platform operators only, gated exactly as Rules is and no
          // second gate on whether the customer's feed sends the figure — an
          // empty table is the answer, and a capability test here would drift
          // out of step with the feed (Aaron, 22 Sep 2026).
          .filter(tab => tab !== 'erp-received' || isSuperuser)
          // Avanti tenants only — the endpoint reports enabled:false elsewhere.
          .filter(tab => tab !== 'data-age' || Boolean(dataAge?.enabled))
          .map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'overview' ? 'Overview'
              : tab === 'data-age' ? 'Data Age'
              : tab === 'jobs' ? 'Recent Jobs'
              : tab === 'writeback' ? 'Writeback'
              : tab === 'lookup' ? 'Order Lookup'
              : tab === 'queues' ? 'Queue Search'
              : tab === 'cancelled' ? 'Auto-Cancelled'
              : tab === 'date-exception' ? 'Date Inconsistency Exception'
              : tab === 'new-po-date-exception' ? 'New PO Date Inconsistency Exception'
              : tab === 'scheduled-jobs' ? 'Scheduled Jobs'
              : tab === 'erp-balance' ? 'ERP Balance'
              : tab === 'erp-received' ? 'ERP Received'
              : tab === 'collab' ? 'Collaboration Holds'
              : tab === 'rules' ? 'Rules & Mappings'
              : 'Health Check'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && summary && (
        <>
          {source === 'pna' && <MiddlewareHealthPanel reports={middleware?.middleware_health} />}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <JobManagementCard jobs={recentJobs} onRefresh={() => fetchData(true)} />
            <MiddlewareStatus data={middleware} />
            <DataTypeChart data={summary.by_data_type} />
            <RecentFailures failures={summary.recent_failures} />
          </div>
        </>
      )}

      {activeTab === 'data-age' && <DataAgeCard data={dataAge} />}

      {activeTab === 'jobs' && (
        <JobsTable
          jobs={recentJobs}
          onSelectJob={setSelectedJobId}
          currentPage={jobsPage}
          totalCount={jobsTotalCount}
          pageSize={jobsPageSize}
          onPageChange={(p) => setJobsPage(p)}
          onPageSizeChange={(s) => { setJobsPageSize(s); setJobsPage(1); }}
          search={jobsSearchInput}
          onSearchChange={handleSearchChange}
          statusFilter={jobsStatusFilter}
          onStatusFilterChange={(s) => { setJobsStatusFilter(s); setJobsPage(1); }}
          dateRange={jobsDateRange}
          onDateRangeChange={(r) => { setJobsDateRange(r); setJobsPage(1); }}
        />
      )}

      {/* Job Detail Modal */}
      <JobDetailModal jobId={selectedJobId} source={source} onClose={() => setSelectedJobId(null)} />

      {activeTab === 'writeback' && writebackEnabled && (
        <ImportWritebackTab
          customerId={customerId}
        />
      )}

      {activeTab === 'lookup' && <ImportOrderLookupTab customerId={customerId} />}

      {activeTab === 'queues' && <ImportQueueSearchTab customerId={customerId} />}

      {activeTab === 'cancelled' && <ImportAutoCancelledTab customerId={customerId} />}

      {/* CR-9031: the old portal's two date exception lists */}
      {activeTab === 'date-exception' && <ImportDateInconsistencyTab customerId={customerId} kind="accepted" />}
      {activeTab === 'new-po-date-exception' && <ImportDateInconsistencyTab customerId={customerId} kind="new" />}
      {/* CR-8213: run log of nightly scripts, 30 days kept */}
      {activeTab === 'scheduled-jobs' && <ImportScheduledJobsTab customerId={customerId} />}

      {activeTab === 'erp-balance' && erpBalanceEnabled && <ImportErpBalanceTab customerId={customerId} />}

      {activeTab === 'erp-received' && isSuperuser && <ImportErpReceivedTab customerId={customerId} />}

      {activeTab === 'collab' && collabHoldsEnabled && <ImportCollabHoldsTab customerId={customerId} />}
      {activeTab === 'rules' && isSuperuser && <ImportRulesTab customerId={customerId} />}

      {activeTab === 'health' && (
        <ImportHealthCheckTab
          customerId={customerId}
          recentFailures={summary?.recent_failures ?? []}
          jobsFailedToday={summary?.jobs_failed_today ?? 0}
          recordsFailedToday={summary?.records_failed_today ?? 0}
          onSelectJob={setSelectedJobId}
        />
      )}
      </>)}
    </div>
  );
}
